import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { BetterDocMedicSelectionDialog } from "@/modules/admin/better-doc/components/better-doc-medic-selection-dialog"
import { collectBetterDocErrorMessages } from "@/modules/admin/better-doc/lib/collect-better-doc-error-messages"
import { BetterDocSessionSummary } from "@/modules/admin/better-doc/services/betterDocSessionQuery"
import { useCancelBetterDocRecommendationMutation } from "@/modules/admin/better-doc/services/useCancelBetterDocRecommendationMutation"
import { BetterDocSessionActionOutcome } from "@/modules/admin/better-doc/types/BetterDocSessionActionOutcome"
import { useConfirmStore } from "@/stores/useConfirmStore"

type BetterDocTestCaseActionsCardProps = {
  session: BetterDocSessionSummary
  onActionCompleted: (outcome: BetterDocSessionActionOutcome) => void
}

export const BetterDocTestCaseActionsCard = ({ session, onActionCompleted }: BetterDocTestCaseActionsCardProps) => {
  const { t } = useTranslation()
  const [cancelRecommendation, isCancellingRecommendation] = useCancelBetterDocRecommendationMutation()
  const [isMedicSelectionDialogOpen, setIsMedicSelectionDialogOpen] = useState(false)
  const confirm = useConfirmStore((state) => state.confirm)
  const closeConfirmation = useConfirmStore((state) => state.cancel)
  const isMedicSelectionAvailable = session.insuranceCountry === "CH" && session.recommendationDelivery === "PMN"

  const submitRecommendationCancellation = () => {
    cancelRecommendation({
      variables: { input: { sessionId: session.sessionId } },
      onCompleted: (response) => {
        const { actionResult, errors } = response.de_cancelBetterDocRecommendation
        onActionCompleted({ result: actionResult, errorMessages: collectBetterDocErrorMessages(errors) })
      },
    })
  }

  const handleCancelRecommendation = () => {
    confirm({
      title: t("admin.betterDoc.testCaseActions.cancelConfirmationTitle", { sessionId: session.sessionId }),
      content: t("admin.betterDoc.testCaseActions.cancelConfirmationContent"),
      cancelText: t("common.button.keep"),
      confirmText: t("admin.betterDoc.testCaseActions.cancelRecommendation"),
      confirmVariant: "danger",
      onConfirm: () => {
        submitRecommendationCancellation()
        closeConfirmation()
      },
    })
  }

  return (
    <Card data-testid="better-doc-admin-test-case-actions">
      <CardHeader>
        <CardHeaderTitle>{t("admin.betterDoc.testCaseActions.title")}</CardHeaderTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2 p-4 pt-0">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            type="button"
            variant="secondary"
            isLoading={isCancellingRecommendation}
            disabled={session.recommendationId === null || isCancellingRecommendation}
            onClick={handleCancelRecommendation}
            data-testid="better-doc-admin-test-case-cancel-recommendation-button">
            {t("admin.betterDoc.testCaseActions.cancelRecommendation")}
          </Button>
          {isMedicSelectionAvailable ? (
            <Button
              type="button"
              variant="secondary"
              disabled={session.recommendationId === null}
              onClick={() => setIsMedicSelectionDialogOpen(true)}
              data-testid="better-doc-admin-test-case-select-medic-button">
              {t("admin.betterDoc.testCaseActions.selectMedic")}
            </Button>
          ) : null}
          {session.recommendationId === null ? (
            <span
              className="text-on-surface-subdued text-xs"
              data-testid="better-doc-admin-test-case-cancel-recommendation-hint">
              {t("admin.betterDoc.noRecommendationYet")}
            </span>
          ) : null}
        </div>
        <p className="text-on-surface-subdued text-sm">{t("admin.betterDoc.testCaseActions.cancelHint")}</p>
        {isMedicSelectionAvailable ? (
          <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-test-case-select-medic-hint">
            {t("admin.betterDoc.testCaseActions.selectMedicHint")}
          </p>
        ) : null}
      </CardBody>
      {isMedicSelectionDialogOpen && isMedicSelectionAvailable && session.recommendationId !== null ? (
        <BetterDocMedicSelectionDialog
          sessionId={session.sessionId}
          recommendationId={session.recommendationId}
          onClose={() => setIsMedicSelectionDialogOpen(false)}
          onCompleted={onActionCompleted}
        />
      ) : null}
    </Card>
  )
}
